import { Copy } from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from './Tooltip';
import { copyToClipboard } from 'src/utils/copyToClipboard';
import { NotificationContextState } from 'src/contexts/NotificationContext';

type CopyableFieldProps = {
  value: string | React.ReactNode;
  notification: NotificationContextState;
  className?: string;
  stripWhitespaces?: boolean;
};

const cleanPhoneNumber = (phoneNumber: string, stripWhitespaces: boolean) => {
  if (stripWhitespaces) {
    return phoneNumber.split(' ').join('');
  }
  return phoneNumber;
};

export const CopyableField = ({
  value,
  notification,
  className,
  stripWhitespaces = false,
}: CopyableFieldProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const textRef = useRef<HTMLDivElement>(null);

  const getTextContent = () => {
    if (typeof value === 'string') {
      return value;
    }
    return (value as React.ReactElement)?.props?.children?.toString() || '';
  };

  const textContent = getTextContent();

  const updateTooltipPosition = () => {
    if (textRef.current) {
      const rect = textRef.current.getBoundingClientRect();

      setTooltipPosition({
        top: rect.top - 40,
        left: rect.left + rect.width / 2,
      });
    }
  };

  const handleMouseEnter = () => {
    updateTooltipPosition();
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  useEffect(() => {
    if (showTooltip) {
      const handleScroll = () => updateTooltipPosition();
      const handleResize = () => updateTooltipPosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showTooltip]);

  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex-1 min-w-0 mr-1 relative flex">
        <div
          ref={textRef}
          className="text-ellipsis whitespace-nowrap overflow-hidden cursor-default"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {value}
        </div>

        {value && (
          <Tooltip content="Copy to clipboard" position="top" zIndex={50}>
            <Copy
              className="cursor-pointer inline-block w-5 h-5 flex-shrink-0 ml-1"
              onClick={(evt) => {
                evt.stopPropagation();
                const text =
                  typeof value === 'string'
                    ? value
                    : (
                        value as React.ReactElement
                      )?.props?.children?.toString() || '';

                const finalText = stripWhitespaces
                  ? cleanPhoneNumber(text, stripWhitespaces)
                  : text;

                copyToClipboard(finalText, notification);
              }}
            />
          </Tooltip>
        )}
      </div>

      {showTooltip &&
        createPortal(
          <div
            className="fixed z-50 bg-neutral-700 text-neutral-0 text-xs rounded py-2 px-3 max-w-xs pointer-events-none"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="break-words">{textContent}</div>
            <div className="absolute w-2 h-2 bg-neutral-700 transform rotate-45 bottom-0 left-1/2 -translate-x-1/2 translate-y-1" />
          </div>,
          document.body,
        )}
    </div>
  );
};
